import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FarmInfo, CreateFarmInput, UpdateFarmInput } from '@grosync/common';
import { createFarm, deleteFarm, fetchFarms, updateFarm } from '../../../api/farms';

const KEY = ['farms'] as const;

export function useFarms() {
  const qc = useQueryClient();

  const query = useQuery({ queryKey: KEY, queryFn: fetchFarms });

  const create = useMutation({
    mutationFn: (body: CreateFarmInput) => createFarm(body),
    onSuccess: (farm) => {
      qc.setQueryData<FarmInfo[]>(KEY, (old = []) => [farm, ...old]);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateFarmInput }) => updateFarm(id, body),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFarm(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<FarmInfo[]>(KEY);
      qc.setQueryData<FarmInfo[]>(KEY, (old = []) => old.filter((f) => f.id !== id));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  return { query, create, update, remove };
}
